import { InternalId, StaticReference } from '@edgebox/data-definition-kit';
import { AccessScope, TaxonomyFacet, TaxonomyTermOrigin, TaxonomyTermStatus } from '@edgebox/sync-core-data-definitions';
import { ClientLocaleEntity, ClientTaxonomyEntity, ClientTaxonomyTermDraft, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { sendToParent } from '../../../frame-messages';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { facetDescription } from './taxonomyFacetMetadata';
import { applyTermFormValues, TaxonomyTermRecord } from './Components/GovernanceTaxonomyTermForm';
import { localeDisplayName, localeReferenceId } from './Components/localeDisplay.utils';
import { applyTermLocale, monitoredCountryOf } from './Components/localeTerm.utils';
import { forgetTaxonomyTermIndexFacets } from './Onboarding/ContentInventory/Components/taxonomyTermIndex';
import {
  BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS,
  BRAND_PRESENCE_ONBOARDING_TOTAL_STEPS,
  brandPresenceOnboardingStepPath,
} from './brandPresencePaths';
import {
  GovernanceAiSuggestedListItem,
  GovernanceAiSuggestionLabel,
  GovernanceOnboardingContainer,
  GovernanceOnboardingListItem,
  GovernanceOnboardingNewListItem,
  GovernanceOnboardingStepContainer,
} from './Onboarding/components/GovernanceOnboardingPrimitives';
import { GovernanceOnboardingListSkeleton } from './Onboarding/components/GovernanceOnboardingSkeleton';

const STEP5_FACETS_IN_ORDER: TaxonomyFacet[] = [
  TaxonomyFacet.Competitor,
  TaxonomyFacet.AuthoritySource,
  TaxonomyFacet.Market,
  TaxonomyFacet.Locale,
  TaxonomyFacet.Persona,
  TaxonomyFacet.FunnelStage,
  TaxonomyFacet.Intent,
];

type TStepMode = 'primary' | 'strategic';
const DEFAULT_VISIBLE_TERMS = 20;

/** Why the strategy step asks for a country and language before the wizard moves on to the prompts. */
const COUNTRY_LANGUAGE_REQUIRED = 'Prompts are asked in the countries and languages added here, so add at least one.';

/** Locales per request, and the most pages read: the list Sync Core keeps is longer than one page. */
const LOCALE_PAGE_SIZE = 500;
const LOCALE_MAX_PAGES = 10;

/** Every locale Sync Core lists, page by page to the last one. */
async function listAllLocales(api: ISyncCoreApi): Promise<ClientLocaleEntity[]> {
  const locales: ClientLocaleEntity[] = [];
  for (let page = 0; page < LOCALE_MAX_PAGES; page += 1) {
    const result = await api.service.locales.list({ page, itemsPerPage: LOCALE_PAGE_SIZE });
    const items = result.items || [];
    locales.push(...items);
    if (!items.length || page + 1 >= (result.numberOfPages ?? 1)) {
      break;
    }
  }
  return locales;
}

interface ITermSuggestion {
  name: string;
  confidence: number;
  aliases?: string[];
}

interface ISelectedTerm {
  name: string;
  aliases?: string[];
}

interface IReducedTermSuggestions {
  suggestionsByTaxonomyKey: Map<string, ITermSuggestion[]>;
  extractedFacets: Set<TaxonomyFacet>;
}

function normalizeTerm(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function parseAliases(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter((entry) => Boolean(entry));
}

function sortStepTaxonomies(mode: TStepMode, entries: ClientTaxonomyEntity[]): ClientTaxonomyEntity[] {
  if (mode === 'strategic') {
    const rank = new Map<TaxonomyFacet, number>(STEP5_FACETS_IN_ORDER.map((facet, index) => [facet, index]));
    return entries
      .filter((entry) => !!entry.facet && rank.has(entry.facet as TaxonomyFacet))
      .sort((a, b) => (rank.get(a.facet as TaxonomyFacet) ?? 999) - (rank.get(b.facet as TaxonomyFacet) ?? 999));
  }

  const strategicSet = new Set<TaxonomyFacet>(STEP5_FACETS_IN_ORDER);
  return entries
    .filter((entry) => !entry.facet || !strategicSet.has(entry.facet as TaxonomyFacet))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
}

function nextPathForMode(mode: TStepMode, onboardingId: string): string {
  return brandPresenceOnboardingStepPath(
    mode === 'primary'
      ? BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.stepTaxonomyTermsStrategic
      : BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.stepPrompts,
    onboardingId
  );
}

function embedNextPathForMode(mode: TStepMode, onboardingId: string): string {
  return nextPathForMode(mode, onboardingId);
}

function backPathForMode(mode: TStepMode, onboardingId: string): string {
  return brandPresenceOnboardingStepPath(
    mode === 'primary'
      ? BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.stepTaxonomies
      : BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.stepTaxonomyTermsPrimary,
    onboardingId
  );
}

function embedBackPathForMode(mode: TStepMode, onboardingId: string): string {
  return backPathForMode(mode, onboardingId);
}

export function parseReducedTermSuggestions(reducedTerms: unknown): IReducedTermSuggestions {
  const suggestionsByTaxonomyKey = new Map<string, ITermSuggestion[]>();
  const extractedFacets = new Set<TaxonomyFacet>();
  const groups = (reducedTerms as { groups?: unknown[] } | undefined)?.groups;
  if (!Array.isArray(groups)) {
    return {
      suggestionsByTaxonomyKey,
      extractedFacets,
    };
  }

  for (const group of groups as Array<{ taxonomyKey?: unknown; taxonomyFacet?: unknown; selectedTerms?: unknown[] }>) {
    const taxonomyKey = String(group.taxonomyKey || '').trim();
    const taxonomyFacet = String(group.taxonomyFacet || '').trim() as TaxonomyFacet;

    if (taxonomyFacet) {
      extractedFacets.add(taxonomyFacet);
    }

    if (!taxonomyKey || !Array.isArray(group.selectedTerms)) {
      continue;
    }

    const suggestions: ITermSuggestion[] = [];
    for (const selectedTerm of group.selectedTerms as Array<{ name?: unknown; aliases?: unknown; sourceOccurrences?: unknown[] }>) {
      const name = normalizeTerm(String(selectedTerm.name || ''));
      if (!name) {
        continue;
      }

      const aliases = parseAliases(selectedTerm.aliases);

      const occurrences = Array.isArray(selectedTerm.sourceOccurrences) ? selectedTerm.sourceOccurrences : [];
      const confidence = Math.max(
        0,
        ...occurrences.map((entry) => {
          const rawConfidence = (entry as { term?: { confidence?: unknown } })?.term?.confidence;
          if (typeof rawConfidence !== 'number' || Number.isNaN(rawConfidence) || !Number.isFinite(rawConfidence)) {
            return 0;
          }

          return rawConfidence >= 0 && rawConfidence <= 1 ? Math.round(rawConfidence * 100) : Math.round(rawConfidence);
        })
      );

      suggestions.push({ name, confidence: Math.max(0, Math.min(100, confidence)), aliases: aliases.length ? aliases : undefined });
    }

    const deduplicated = Array.from(new Map(suggestions.map((entry) => [entry.name.toLowerCase(), entry])).values());
    deduplicated.sort((a, b) => b.confidence - a.confidence || a.name.localeCompare(b.name));
    suggestionsByTaxonomyKey.set(taxonomyKey, deduplicated);
  }

  return {
    suggestionsByTaxonomyKey,
    extractedFacets,
  };
}

function OnboardingTaxonomyTermsStep({ mode }: { mode: TStepMode }) {
  const navigate = useNavigate();
  const apiContext = React.useContext(SyncCoreApiContext);
  const params = useParams<{ onboardingId?: string }>();
  const [searchParams] = useSearchParams();
  const taskGroupId = params.onboardingId || searchParams.get('taskGroupId') || undefined;

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [taxonomies, setTaxonomies] = React.useState<ClientTaxonomyEntity[]>([]);
  const [suggestedTermsByKey, setSuggestedTermsByKey] = React.useState<Map<string, ITermSuggestion[]>>(new Map());
  const [selectedTermsByFacet, setSelectedTermsByFacet] = React.useState<Partial<Record<TaxonomyFacet, ISelectedTerm[]>>>({});
  const [lockedTermsByFacet, setLockedTermsByFacet] = React.useState<Partial<Record<TaxonomyFacet, ISelectedTerm[]>>>({});
  const [ignoredSuggestionsByFacet, setIgnoredSuggestionsByFacet] = React.useState<Partial<Record<TaxonomyFacet, string[]>>>({});
  const [newTermByFacet, setNewTermByFacet] = React.useState<Partial<Record<TaxonomyFacet, string>>>({});
  const [showAllSuggestionsByFacet, setShowAllSuggestionsByFacet] = React.useState<Partial<Record<TaxonomyFacet, boolean>>>({});
  const [showAllSelectedByFacet, setShowAllSelectedByFacet] = React.useState<Partial<Record<TaxonomyFacet, boolean>>>({});
  // The strategy step also collects the countries and languages the prompts are asked in: the
  // locales Sync Core can pair with a monitored country, the ones the project already has, and the
  // ones chosen here. The prompts step cannot create a set without one.
  const asksForCountryLanguage = mode === 'strategic';
  const [localeTaxonomy, setLocaleTaxonomy] = React.useState<ClientTaxonomyEntity | undefined>(undefined);
  const [offeredLocales, setOfferedLocales] = React.useState<ClientLocaleEntity[]>([]);
  const [configuredLocaleIds, setConfiguredLocaleIds] = React.useState<string[]>([]);
  const [chosenLocaleIds, setChosenLocaleIds] = React.useState<string[]>([]);
  const [pendingLocaleId, setPendingLocaleId] = React.useState('');
  // A choice picked and still waiting for its Add counts as chosen: Continue adds it rather than dropping it.
  const chosenOrStagedLocaleIds =
    pendingLocaleId && !chosenLocaleIds.includes(pendingLocaleId) ? [...chosenLocaleIds, pendingLocaleId] : chosenLocaleIds;
  const hasCountryLanguage = configuredLocaleIds.length + chosenOrStagedLocaleIds.length > 0;
  // A read that failed is asked again from here; a failed save is retried by continuing again.
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [loadAttempt, setLoadAttempt] = React.useState(0);
  const autoForwardedRef = React.useRef(false);

  const title = mode === 'primary' ? 'Define topics and dimensions' : 'Refine your strategy and audience';
  const description =
    mode === 'primary'
      ? 'Choose the focus areas for your reporting. You can still add more terms later.'
      : 'Review and customize the sources, market landscape, and audience profiles for your space.';

  React.useEffect(() => {
    if (!apiContext?.api || !taskGroupId) {
      setLoading(false);
      setError('Missing onboarding context.');
      return;
    }

    let active = true;
    void (async () => {
      setLoading(true);
      setError(undefined);
      setLoadFailed(false);

      try {
        const [taskGroup, taxonomyResult, reducedTermsResult, localeResult] = await Promise.all([
          apiContext.api.service.taskGroups.item(taskGroupId as InternalId, true),
          apiContext.api.service.taxonomies.list({ page: 0, itemsPerPage: 500 }, undefined),
          apiContext.api.governance.onboarding.reducedTerms({ taskGroupId: taskGroupId as InternalId }),
          asksForCountryLanguage ? listAllLocales(apiContext.api) : undefined,
        ]);

        if (!active) {
          return;
        }

        const reducedTermSuggestions = parseReducedTermSuggestions(reducedTermsResult);
        const taxonomyEntries = (taxonomyResult.items || []).filter((taxonomy) => {
          if (!taxonomy.facet) {
            return false;
          }

          return reducedTermSuggestions.extractedFacets.has(taxonomy.facet as TaxonomyFacet);
        });

        // Locale tags are chosen from the locales below rather than from what the extraction named:
        // a locale tag Sync Core can use is backed by a locale, and a suggested name carries none.
        const stepTaxonomies = sortStepTaxonomies(mode, taxonomyEntries).filter(
          (taxonomy) => !asksForCountryLanguage || taxonomy.facet !== TaxonomyFacet.Locale
        );
        const projectId = taskGroup.project?.getId();

        if (asksForCountryLanguage) {
          const offered = (localeResult || [])
            .filter((locale) => !!monitoredCountryOf(locale))
            .sort((left, right) => localeDisplayName(left).localeCompare(localeDisplayName(right)));
          const offeredIds = new Set(offered.map((locale) => String(locale.id)));
          const existingLocaleTags = projectId
            ? await apiContext.api.service.taxonomyTerms.list(
                { page: 0, itemsPerPage: 100 },
                { projectId, taxonomyFacet: TaxonomyFacet.Locale }
              )
            : undefined;

          if (!active) {
            return;
          }

          const configured = (existingLocaleTags?.items || [])
            .map((term) => localeReferenceId(term.source?.locale))
            .filter((localeId): localeId is string => !!localeId && offeredIds.has(localeId));

          setLocaleTaxonomy((taxonomyResult.items || []).find((taxonomy) => taxonomy.facet === TaxonomyFacet.Locale));
          setOfferedLocales(offered);
          setConfiguredLocaleIds(Array.from(new Set(configured)));
        }

        const existingTermsByFacet = new Map<TaxonomyFacet, string[]>();

        if (projectId) {
          const existingTermLists = await Promise.all(
            stepTaxonomies.map(async (taxonomy) => {
              if (!taxonomy.facet) {
                return { facet: undefined, terms: [] as string[] };
              }

              const facet = taxonomy.facet as TaxonomyFacet;
              const existingTermsResult = await apiContext.api.service.taxonomyTerms.list(
                { page: 0, itemsPerPage: 100 },
                { projectId, taxonomyFacet: facet }
              );

              const terms = Array.from(
                new Set(
                  (existingTermsResult.items || [])
                    .map((entry) => normalizeTerm(String(entry.name || '')))
                    .filter((entry) => Boolean(entry))
                )
              );

              return { facet, terms };
            })
          );

          for (const entry of existingTermLists) {
            if (entry.facet) {
              existingTermsByFacet.set(entry.facet, entry.terms);
            }
          }
        }

        const nextSelectedTermsByFacet: Partial<Record<TaxonomyFacet, ISelectedTerm[]>> = {};
        const nextLockedTermsByFacet: Partial<Record<TaxonomyFacet, ISelectedTerm[]>> = {};

        for (const taxonomy of stepTaxonomies) {
          if (!taxonomy.facet) {
            continue;
          }

          const facet = taxonomy.facet as TaxonomyFacet;
          const suggestionsForTaxonomy = reducedTermSuggestions.suggestionsByTaxonomyKey.get(taxonomy.key) || [];
          const existingTerms = existingTermsByFacet.get(facet) || [];
          const existingTermByLower = new Map(existingTerms.map((term) => [term.toLowerCase(), term]));
          const matchedExistingTerms: ISelectedTerm[] = suggestionsForTaxonomy
            .filter((suggestion) => existingTermByLower.has(suggestion.name.toLowerCase()))
            .map((suggestion) => existingTermByLower.get(suggestion.name.toLowerCase()) as string)
            .filter((value, index, array) => array.findIndex((entry) => entry.toLowerCase() === value.toLowerCase()) === index)
            .map((name) => ({ name, aliases: undefined }));

          if (matchedExistingTerms.length) {
            nextSelectedTermsByFacet[facet] = matchedExistingTerms;
            nextLockedTermsByFacet[facet] = matchedExistingTerms;
          }
        }

        setTaxonomies(stepTaxonomies);
        setSuggestedTermsByKey(reducedTermSuggestions.suggestionsByTaxonomyKey);
        setSelectedTermsByFacet(nextSelectedTermsByFacet);
        setLockedTermsByFacet(nextLockedTermsByFacet);
      } catch (loadError) {
        if (!active) {
          return;
        }

        setError((loadError as Error)?.message || 'Unable to load taxonomy terms step.');
        setLoadFailed(true);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [apiContext?.api, asksForCountryLanguage, loadAttempt, mode, taskGroupId]);

  React.useEffect(() => {
    // A step with nothing to review moves on by itself. The strategy step always has the countries and
    // languages to review, and a step that could not load has nothing it could have moved on from.
    if (!taskGroupId || loading || !!error || taxonomies.length > 0 || asksForCountryLanguage || autoForwardedRef.current) {
      return;
    }

    autoForwardedRef.current = true;
    const nextPath = nextPathForMode(mode, taskGroupId);
    const nextEmbedPath = embedNextPathForMode(mode, taskGroupId);
    sendToParent({ type: 'update-path', path: nextPath });
    navigate(nextEmbedPath, { replace: true });
  }, [asksForCountryLanguage, error, loading, mode, navigate, taskGroupId, taxonomies.length]);

  const addTerm = (facet: TaxonomyFacet, value: string, aliases?: string[]) => {
    const term = normalizeTerm(value);
    if (!term) {
      return;
    }

    setSelectedTermsByFacet((prev) => {
      const current = prev[facet] || [];
      if (current.some((entry) => entry.name.toLowerCase() === term.toLowerCase())) {
        return prev;
      }

      return {
        ...prev,
        [facet]: [...current, { name: term, aliases: aliases?.length ? aliases : undefined }],
      };
    });
  };

  const removeTerm = (facet: TaxonomyFacet, name: string) => {
    if ((lockedTermsByFacet[facet] || []).some((entry) => entry.name.toLowerCase() === name.toLowerCase())) {
      return;
    }

    setSelectedTermsByFacet((prev) => ({
      ...prev,
      [facet]: (prev[facet] || []).filter((entry) => entry.name.toLowerCase() !== name.toLowerCase()),
    }));
  };

  const removeAlias = (facet: TaxonomyFacet, termName: string, alias: string) => {
    setSelectedTermsByFacet((prev) => ({
      ...prev,
      [facet]: (prev[facet] || []).map((entry) =>
        entry.name.toLowerCase() === termName.toLowerCase()
          ? { ...entry, aliases: (entry.aliases || []).filter((value) => value !== alias) }
          : entry
      ),
    }));
  };

  const ignoreSuggestion = (facet: TaxonomyFacet, value: string) => {
    const term = normalizeTerm(value);
    if (!term) {
      return;
    }

    setIgnoredSuggestionsByFacet((prev) => {
      const current = prev[facet] || [];
      if (current.some((entry) => entry.toLowerCase() === term.toLowerCase())) {
        return prev;
      }

      return {
        ...prev,
        [facet]: [...current, term],
      };
    });
  };

  const onContinue = async () => {
    if (!apiContext?.api || !taskGroupId) {
      return;
    }

    setSaving(true);
    setError(undefined);
    let wroteLocales = false;
    try {
      const taskGroup = await apiContext.api.service.taskGroups.item(taskGroupId as InternalId, true);
      const project = await taskGroup.project?.get();
      const projectId = project?.id as InternalId | undefined;
      if (!projectId) {
        throw new Error('Could not resolve project for taxonomy terms.');
      }

      for (const taxonomy of taxonomies) {
        const facet = taxonomy.facet as TaxonomyFacet;
        const selectedTerms = selectedTermsByFacet[facet] || [];
        if (!selectedTerms.length) {
          continue;
        }

        const existingTermsResult = await apiContext.api.service.taxonomyTerms.list(
          { page: 0, itemsPerPage: 500 },
          { projectId, taxonomyFacet: facet }
        );
        const existingTerms = new Set((existingTermsResult.items || []).map((entry) => entry.name.toLowerCase()));

        for (const entry of selectedTerms) {
          if (existingTerms.has(entry.name.toLowerCase())) {
            continue;
          }

          const draft = new ClientTaxonomyTermDraft();
          draft.accessScope = AccessScope.Project;
          draft.origin = TaxonomyTermOrigin.User;
          draft.status = TaxonomyTermStatus.Active;
          draft.name = entry.name;
          draft.aliases = entry.aliases?.length ? entry.aliases : undefined;
          draft.taxonomy = new StaticReference(taxonomy);

          if (taskGroup.customer) {
            draft.customer = taskGroup.customer;
          }

          if (taskGroup.project) {
            draft.project = taskGroup.project;
          }

          await apiContext.api.service.taxonomyTerms.create(draft);
        }
      }

      // Each chosen country and language becomes a locale tag backed by its locale, the way the
      // settings create one, so Sync Core can pair it with its country.
      const localesToCreate = chosenOrStagedLocaleIds
        .filter((localeId) => !configuredLocaleIds.includes(localeId))
        .map((localeId) => offeredLocales.find((locale) => String(locale.id) === localeId))
        .filter((locale): locale is ClientLocaleEntity => !!locale);
      if (localesToCreate.length) {
        if (!localeTaxonomy) {
          throw new Error('The countries and languages could not be saved for this project.');
        }

        for (const locale of localesToCreate) {
          const draft = new ClientTaxonomyTermDraft();
          draft.accessScope = AccessScope.Project;
          draft.status = TaxonomyTermStatus.Active;
          draft.taxonomy = new StaticReference(localeTaxonomy);
          applyTermFormValues(draft as TaxonomyTermRecord, { name: localeDisplayName(locale) }, TaxonomyFacet.Locale);
          applyTermLocale(draft, locale);

          if (taskGroup.customer) {
            draft.customer = taskGroup.customer;
          }

          if (taskGroup.project) {
            draft.project = taskGroup.project;
          }

          wroteLocales = true;
          await apiContext.api.service.taxonomyTerms.create(draft);
          // Counted as the project's from here on, so a retry after a later failure does not create it twice.
          setConfiguredLocaleIds((current) => [...current, String(locale.id)]);
        }
      }

      const nextPath = nextPathForMode(mode, taskGroupId);
      const nextEmbedPath = embedNextPathForMode(mode, taskGroupId);
      sendToParent({ type: 'update-path', path: nextPath });
      navigate(nextEmbedPath);
    } catch (continueError) {
      setError((continueError as Error)?.message || 'Unable to save selected terms.');
    } finally {
      // The rest of the frame reads these vocabularies from a shared cache that holds none of the tags
      // written here, including the ones written before a later write failed; the prompts step names
      // them next.
      forgetTaxonomyTermIndexFacets([
        ...taxonomies.map((taxonomy) => taxonomy.facet as TaxonomyFacet),
        ...(wroteLocales ? [TaxonomyFacet.Locale] : []),
      ]);
      setSaving(false);
    }
  };

  const addPendingLocale = () => {
    if (!pendingLocaleId) {
      return;
    }

    setChosenLocaleIds((current) => (current.includes(pendingLocaleId) ? current : [...current, pendingLocaleId]));
    setPendingLocaleId('');
  };

  const addableLocales = offeredLocales.filter(
    (locale) => !configuredLocaleIds.includes(String(locale.id)) && !chosenLocaleIds.includes(String(locale.id))
  );

  return (
    <GovernanceOnboardingContainer>
      <GovernanceOnboardingStepContainer
        step={mode === 'primary' ? 4 : 5}
        totalSteps={BRAND_PRESENCE_ONBOARDING_TOTAL_STEPS}
        title={title}
        description={description}
        onBack={() => {
          if (!taskGroupId) {
            return;
          }

          const backPath = backPathForMode(mode, taskGroupId);
          const backEmbedPath = embedBackPathForMode(mode, taskGroupId);
          sendToParent({ type: 'update-path', path: backPath });
          navigate(backEmbedPath, { replace: true });
        }}
        onContinue={() => {
          void onContinue();
        }}
        continueLabel={saving ? 'Saving…' : 'Continue'}
        continueDisabled={loading || saving || loadFailed || (asksForCountryLanguage && !hasCountryLanguage)}
      >
        <div className="w-full space-y-6">
          {loading ? <GovernanceOnboardingListSkeleton label="Loading taxonomy terms…" /> : null}

          {error ? (
            <div className="space-y-2">
              <div className="text-sm text-danger">{error}</div>
              {loadFailed ? (
                <button
                  type="button"
                  className="text-sm font-semibold text-primary hover:underline"
                  onClick={() => setLoadAttempt((count) => count + 1)}
                >
                  Try again
                </button>
              ) : null}
            </div>
          ) : null}

          {asksForCountryLanguage && !loading && !loadFailed ? (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-slate-900">Countries and languages</h3>
              <p className="text-sm text-slate-500">{COUNTRY_LANGUAGE_REQUIRED}</p>

              {offeredLocales.length ? (
                <GovernanceOnboardingNewListItem onDelete={() => setPendingLocaleId('')} onSave={addPendingLocale} saveLabel="Add">
                  <div className="col-span-12 md:col-span-10">
                    <select
                      aria-label="Country and language"
                      value={pendingLocaleId}
                      disabled={saving || !addableLocales.length}
                      onChange={(event) => setPendingLocaleId(event.target.value)}
                      className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                    >
                      <option value="">Choose a country and language</option>
                      {addableLocales.map((locale) => (
                        <option key={String(locale.id)} value={String(locale.id)}>
                          {localeDisplayName(locale)}
                        </option>
                      ))}
                    </select>
                  </div>
                </GovernanceOnboardingNewListItem>
              ) : (
                <p className="text-sm text-slate-500">No country and language can be added to this project yet.</p>
              )}

              {offeredLocales
                .filter((locale) => configuredLocaleIds.includes(String(locale.id)) || chosenLocaleIds.includes(String(locale.id)))
                .map((locale) => {
                  const localeId = String(locale.id);
                  const isConfigured = configuredLocaleIds.includes(localeId);

                  return (
                    <GovernanceOnboardingListItem
                      key={`locale:${localeId}`}
                      deleteLabel={`Remove ${localeDisplayName(locale)}`}
                      onDelete={
                        isConfigured ? undefined : () => setChosenLocaleIds((current) => current.filter((entry) => entry !== localeId))
                      }
                    >
                      <div className="col-span-12 md:col-span-10 text-left">
                        <div className="font-semibold text-slate-900 truncate">{localeDisplayName(locale)}</div>
                        {isConfigured ? <div className="text-sm text-slate-500 truncate">Already configured</div> : null}
                      </div>
                    </GovernanceOnboardingListItem>
                  );
                })}
            </div>
          ) : null}

          {taxonomies.map((taxonomy) => {
            const facet = taxonomy.facet as TaxonomyFacet;
            const suggestions = suggestedTermsByKey.get(taxonomy.key) || [];
            const selectedTerms = selectedTermsByFacet[facet] || [];
            const lockedTerms = lockedTermsByFacet[facet] || [];
            const ignoredTerms = ignoredSuggestionsByFacet[facet] || [];
            const shownSuggestions = suggestions.filter((suggestion) => {
              const lower = suggestion.name.toLowerCase();
              return (
                !selectedTerms.some((entry) => entry.name.toLowerCase() === lower) &&
                !ignoredTerms.some((entry) => entry.toLowerCase() === lower)
              );
            });
            const shouldLimitTerms = mode === 'strategic';
            const visibleSuggestions =
              shouldLimitTerms && !showAllSuggestionsByFacet[facet] ? shownSuggestions.slice(0, DEFAULT_VISIBLE_TERMS) : shownSuggestions;
            const visibleSelectedTerms =
              shouldLimitTerms && !showAllSelectedByFacet[facet] ? selectedTerms.slice(0, DEFAULT_VISIBLE_TERMS) : selectedTerms;

            return (
              <div key={taxonomy.id} className="space-y-3">
                <h3 className="text-lg font-semibold text-slate-900">{taxonomy.name}</h3>
                <p className="text-sm text-slate-500">{facetDescription(facet, taxonomy.name)}</p>

                {visibleSuggestions.map((suggestion) => (
                  <GovernanceAiSuggestedListItem
                    key={`${taxonomy.facet}:${suggestion.name}`}
                    onAccept={() => addTerm(taxonomy.facet, suggestion.name, suggestion.aliases)}
                    onIgnore={() => ignoreSuggestion(taxonomy.facet, suggestion.name)}
                  >
                    <div className="col-span-12 md:col-span-10 text-left pl-2 md:pl-0">
                      <div className="flex items-center gap-2 min-w-0 flex-wrap md:flex-nowrap">
                        <div className="font-semibold text-slate-900 truncate">{suggestion.name}</div>
                        <GovernanceAiSuggestionLabel />
                        <span className="inline-flex shrink-0 whitespace-nowrap rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-inset ring-slate-200">
                          {suggestion.confidence}%
                        </span>
                      </div>
                    </div>
                  </GovernanceAiSuggestedListItem>
                ))}

                {shouldLimitTerms && shownSuggestions.length > DEFAULT_VISIBLE_TERMS ? (
                  <div className="text-right">
                    <button
                      type="button"
                      className="text-sm font-semibold text-primary hover:underline"
                      onClick={() =>
                        setShowAllSuggestionsByFacet((prev) => ({
                          ...prev,
                          [facet]: !prev[facet],
                        }))
                      }
                    >
                      {showAllSuggestionsByFacet[facet] ? 'Show fewer suggestions' : `Show all suggestions (${shownSuggestions.length})`}
                    </button>
                  </div>
                ) : null}

                <GovernanceOnboardingNewListItem
                  onDelete={() => {
                    setNewTermByFacet((prev) => ({
                      ...prev,
                      [facet]: '',
                    }));
                  }}
                  onSave={() => {
                    addTerm(facet, newTermByFacet[facet] || '');
                    setNewTermByFacet((prev) => ({
                      ...prev,
                      [facet]: '',
                    }));
                  }}
                >
                  <div className="col-span-12 md:col-span-10">
                    <input
                      type="text"
                      value={newTermByFacet[facet] || ''}
                      placeholder="Add custom term"
                      onChange={(event) =>
                        setNewTermByFacet((prev) => ({
                          ...prev,
                          [facet]: event.target.value,
                        }))
                      }
                      className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                    />
                  </div>
                </GovernanceOnboardingNewListItem>

                {visibleSelectedTerms.map((term) => {
                  const isLocked = lockedTerms.some((entry) => entry.name.toLowerCase() === term.name.toLowerCase());

                  return (
                    <GovernanceOnboardingListItem
                      key={`${facet}:${term.name}`}
                      onDelete={
                        isLocked
                          ? undefined
                          : () => {
                              removeTerm(facet, term.name);
                            }
                      }
                    >
                      <div className="col-span-12 md:col-span-10 text-left">
                        <div className="font-semibold text-slate-900 truncate">{term.name}</div>
                        {isLocked ? <div className="text-sm text-slate-500 truncate">Already configured</div> : null}
                        {term.aliases?.length ? (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {term.aliases.map((alias) => (
                              <span
                                key={`${facet}:${term.name}:${alias}`}
                                className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-600 ring-1 ring-inset ring-slate-200"
                              >
                                <span className="truncate">{alias}</span>
                                <button
                                  type="button"
                                  onClick={() => removeAlias(facet, term.name, alias)}
                                  className="text-slate-400 hover:text-red-500 transition-colors"
                                  aria-label={`Remove alias ${alias}`}
                                >
                                  <FontAwesomeIcon icon={faXmark} />
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </GovernanceOnboardingListItem>
                  );
                })}

                {shouldLimitTerms && selectedTerms.length > DEFAULT_VISIBLE_TERMS ? (
                  <div className="text-right">
                    <button
                      type="button"
                      className="text-sm font-semibold text-primary hover:underline"
                      onClick={() =>
                        setShowAllSelectedByFacet((prev) => ({
                          ...prev,
                          [facet]: !prev[facet],
                        }))
                      }
                    >
                      {showAllSelectedByFacet[facet] ? 'Show fewer selected terms' : `Show all selected terms (${selectedTerms.length})`}
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </GovernanceOnboardingStepContainer>
    </GovernanceOnboardingContainer>
  );
}

export function GovernanceOnboardingStepTaxonomyTermsPrimary() {
  return <OnboardingTaxonomyTermsStep mode="primary" />;
}

export function GovernanceOnboardingStepTaxonomyTermsStrategic() {
  return <OnboardingTaxonomyTermsStep mode="strategic" />;
}
